import {
  makeObservable,
  action,
  observable,
  computed,
  runInAction,
} from "mobx";
import SwagStoreAPI from "../api/SwagStoreAPI";
import { SwagstoreRequestBody } from "../models/Swagstore/SwagstoreRequestBody";
import { ThemeRequestBody } from "../models/Swagstore/ThemeRequestBody";
import { ProductMappingRequestBody } from "../models/Swagstore/ProductMappingRequestBody";
import {
  APIBlobResponse,
  APIResponse,
  PaginatedAPIResponse,
} from "../models/NetworkResponse";
import {
  SwagstoreSettingsModel,
  createEmptySwagstoreSettings,
} from "../models/Swagstore/SwagstoreSettingsModel";
import { SwagstoreProductCategory } from "../models/Swagstore/ProductMappingModel";
import {
  SWAGSTORE_STEPS,
  SwagstoreStep,
} from "../constants/SwagstoreConstants";
import { Swagpack } from "../models/Swagstore/Packs";
import { SwagstoreFilters } from "../models/Swagstore/SwagstoreFilters";
import { SwagstoreWalletTransaction } from "../models/Swagstore/SwagstoreWallet";
import { FilterValue } from "../models/Filters/FilterValue";
import { SortingOptions } from "../models/Filters/FilterOptions";
import ImageAPI from "../api/ImageAPI";

export type DeviceView = "desktop" | "tablet" | "mobile";
export type SelectedItem = SwagstoreStep;

export class SwagStoreStore {
  swagStoreAPI: SwagStoreAPI;

  // UI State
  selectedItem: SelectedItem = SWAGSTORE_STEPS.STORE_DETAILS;
  deviceView: DeviceView = "desktop";
  leftDrawerOpen: boolean = false;
  rightDrawerOpen: boolean = false;

  // Settings State
  settings: SwagstoreSettingsModel = createEmptySwagstoreSettings();
  storeId: string | null = null;
  themeId: number | null = null;
  storeDetailsErrors: Record<string, string> = {};

  // Product Settings State
  productSearchTerm: string = "";
  selectedCategory: string = "All";
  selectedProductId: number | null = null;
  modalOpen: boolean = false;
  inventorySearchTerm: string = "";
  catalogueSearchTerm: string = "";
  selectedCatalogueCategory: string = "All";
  selectedCatalogueCategoryIds: number[] = [];
  selectedSource: "inventory" | "catalogue" | "csv" = "inventory";
  dragOver: boolean = false;
  inventoryProducts: Swagpack[] = [];
  fetchedInventoryProducts: Swagpack[] = [];
  fetchedCatalogueProducts: Swagpack[] = [];
  loadingInventory: boolean = false;
  loadingCatalogue: boolean = false;
  hasLoadedCatalogue: boolean = false;
  catalogueLoadedForKey: string | null = null;
  uploadedMockupLogos: Array<{ id: number; logo_url: string }> = [];
  loadingUploadedMockupLogos: boolean = false;
  mockupLogosLoadedForKey: string | null = null;
  private catalogueRequestSeq: number = 0;
  private activeCatalogueRequestSeq: number = 0;
  selectedInventoryProducts: number[] = [];
  selectedCatalogueProducts: number[] = [];
  productsToDisplayOnProductPage: Swagpack[] = [];
  baseProductsToDisplay: Swagpack[] = []; // Base products list that doesn't change with filters
  /** Paginated catalogue rows for Mockups step only (does not replace fetchedCatalogueProducts). */
  mockupsCatalogueProducts: Swagpack[] = [];
  loadingMockupsCatalogue = false;
  mockupsCatalogueHydratedKey: string | null = null;
  mockupsCataloguePageCache: Record<
    string,
    {
      products: Swagpack[];
      totalCount: number;
      totalPages: number;
      hasNextPage: boolean;
    }
  > = {};
  mockupsCatalogueHasNextPage = false;
  mockupsCatalogueTotalCount = 0;
  mockupsCatalogueTotalPages = 0;
  mockupsCatalogueCountsHydratedKey: string | null = null;
  mockupsCatalogueStatusCounts: Record<
    | "all"
    | "pending"
    | "changes_requested"
    | "approved"
    | "not_uploaded"
    | "deleted",
    number
  > = {
    all: 0,
    pending: 0,
    changes_requested: 0,
    approved: 0,
    not_uploaded: 0,
    deleted: 0,
  };
  mockupsLocalStatusOverrides: Record<
    number,
    {
      status:
        | "pending"
        | "changes_requested"
        | "approved"
        | "not_uploaded"
        | "deleted";
      product: Swagpack;
    }
  > = {};
  mockupsLocalDeletedProductIds: Record<number, boolean> = {};
  categories: SwagstoreProductCategory[] = [];

  get filteredCatalogueProducts(): Swagpack[] {
    if (this.selectedCatalogueCategoryIds.length === 0) {
      return this.fetchedCatalogueProducts;
    }
    return this.fetchedCatalogueProducts.filter(
      (product) =>
        product.categories?.some((c) =>
          this.selectedCatalogueCategoryIds.includes(c.category_id),
        ) ?? false,
    );
  }

  editedPackPrices: Record<number, number | null> = {};
  editedProductPrices: Record<number, number | null> = {};
  editedProductLimitedStock: Record<number, boolean | undefined> = {};

  // Track variant quantities: pack_id -> variant_id -> quantity (for inventory packs)
  editedVariantQuantities: Record<number, Record<number, number | null>> = {};
  // Track variant quantities: product_id -> variant_id -> quantity (for catalogue products)
  editedProductVariantQuantities: Record<
    number,
    Record<number, number | null>
  > = {};
  editedProductQuantities: Record<number, number | null> = {};

  inventoryPage: number = 1;
  inventoryLimit: number = 20;
  inventoryTotalPages: number = 0;
  inventoryTotalCount: number = 0;

  cataloguePage: number = 1;
  catalogueLimit: number = 20;
  catalogueTotalPages: number = 0;
  catalogueTotalCount: number = 0;
  selectedOrderIds: FilterValue[] = [];
  orderIdSearchTerm: string = "";
  inventorySortBy: string | null = "quantity";
  inventorySortType: SortingOptions | null = SortingOptions.ASC;

  // Store Listing State
  stores: any[] = [];
  storeListingPage: number = 1;
  storeListingLimit: number = 30;
  storeListingTotalPages: number = 0;
  storeListingTotalCount: number = 0;
  loadingStores: boolean = false;
  storeListingSearchText: string = "";
  addBalanceStoreId: number | string | null = null;
  addBalanceWalletLoading: boolean = false;
  addBalanceSubmitting: boolean = false;
  addBalanceCurrentBalance: number | null = null;
  addBalanceWalletError: string | null = null;
  walletBalanceByCustomerId: Record<number, number> = {};
  walletHistoryStoreId: number | string | null = null;
  walletTransactions: SwagstoreWalletTransaction[] = [];
  walletTransactionsLoading: boolean = false;
  walletTransactionsError: string | null = null;
  walletTransactionsPage: number = 1;
  walletTransactionsLimit: number = 20;
  walletTransactionsTotalCount: number = 0;

  // Authentication Methods State
  authenticationMethods: Array<{
    id: number;
    name: string;
    label: string;
  }> = [];
  selectedAuthenticationMethods: number[] = [];
  loadingAuthenticationMethods: boolean = false;

  constructor() {
    this.swagStoreAPI = new SwagStoreAPI();

    makeObservable(this, {
      // UI State
      selectedItem: observable,
      deviceView: observable,
      leftDrawerOpen: observable,
      rightDrawerOpen: observable,

      // Settings State
      settings: observable,

      // Product Settings State
      productSearchTerm: observable,
      selectedCategory: observable,
      selectedProductId: observable,
      modalOpen: observable,
      inventorySearchTerm: observable,
      catalogueSearchTerm: observable,
      selectedCatalogueCategory: observable,
      selectedCatalogueCategoryIds: observable,
      selectedSource: observable,
      dragOver: observable,
      inventoryProducts: observable,
      fetchedInventoryProducts: observable,
      fetchedCatalogueProducts: observable,
      filteredCatalogueProducts: computed,
      loadingInventory: observable,
      loadingCatalogue: observable,
      hasLoadedCatalogue: observable,
      catalogueLoadedForKey: observable,
      uploadedMockupLogos: observable,
      loadingUploadedMockupLogos: observable,
      mockupLogosLoadedForKey: observable,
      selectedInventoryProducts: observable,
      selectedCatalogueProducts: observable,
      productsToDisplayOnProductPage: observable,
      baseProductsToDisplay: observable,
      mockupsCatalogueProducts: observable,
      loadingMockupsCatalogue: observable,
      mockupsCatalogueHydratedKey: observable,
      mockupsCatalogueHasNextPage: observable,
      mockupsCatalogueTotalCount: observable,
      mockupsCatalogueTotalPages: observable,
      mockupsCatalogueStatusCounts: observable,
      mockupsLocalStatusOverrides: observable,
      mockupsLocalDeletedProductIds: observable,
      categories: observable,
      editedPackPrices: observable,
      editedProductPrices: observable,
      editedProductLimitedStock: observable,
      editedVariantQuantities: observable,
      editedProductVariantQuantities: observable,
      editedProductQuantities: observable,
      inventoryPage: observable,
      inventoryLimit: observable,
      inventoryTotalPages: observable,
      inventoryTotalCount: observable,
      cataloguePage: observable,
      catalogueLimit: observable,
      catalogueTotalPages: observable,
      catalogueTotalCount: observable,
      selectedOrderIds: observable,
      orderIdSearchTerm: observable,
      inventorySortBy: observable,
      inventorySortType: observable,
      setProductsToDisplayOnProductPage: action,
      fetchMockupsCataloguePage: action,
      resetMockupsCatalogueList: action,
      applyLocalMockupStatusTransition: action,
      markProductAsLocallyDeleted: action,

      // Store Listing State
      stores: observable,
      storeListingPage: observable,
      storeListingLimit: observable,
      storeListingTotalPages: observable,
      storeListingTotalCount: observable,
      loadingStores: observable,
      storeListingSearchText: observable,
      addBalanceStoreId: observable,
      addBalanceWalletLoading: observable,
      addBalanceSubmitting: observable,
      addBalanceCurrentBalance: observable,
      addBalanceWalletError: observable,
      walletBalanceByCustomerId: observable,
      walletHistoryStoreId: observable,
      walletTransactions: observable,
      walletTransactionsLoading: observable,
      walletTransactionsError: observable,
      walletTransactionsPage: observable,
      walletTransactionsLimit: observable,
      walletTransactionsTotalCount: observable,

      // Authentication Methods State
      authenticationMethods: observable,
      selectedAuthenticationMethods: observable,
      loadingAuthenticationMethods: observable,

      // Actions
      setSelectedItem: action,
      setDeviceView: action,
      setLeftDrawerOpen: action,
      setRightDrawerOpen: action,
      updateStoreDetails: action,
      updateBranding: action,
      updateLoginPage: action,
      updateProductMapping: action,
      setProductSearchTerm: action,
      setSelectedCategory: action,
      setSelectedProductId: action,
      setModalOpen: action,
      setInventorySearchTerm: action,
      setCatalogueSearchTerm: action,
      setSelectedCatalogueCategory: action,
      setSelectedCatalogueCategoryIds: action,
      clearSelectedCatalogueCategoryIds: action,
      setSelectedSource: action,
      setDragOver: action,
      setInventoryProducts: action,
      setFetchedInventoryProducts: action,
      setFetchedCatalogueProducts: action,
      setLoadingInventory: action,
      setLoadingCatalogue: action,
      setHasLoadedCatalogue: action,
      setCatalogueLoadedForKey: action,
      setUploadedMockupLogos: action,
      setLoadingUploadedMockupLogos: action,
      setMockupLogosLoadedForKey: action,
      loadUploadedMockupLogos: action,
      uploadMockupLogo: action,
      deleteMockupLogo: action,
      toggleInventoryProduct: action,
      toggleCatalogueProduct: action,
      selectAllInventoryProducts: action,
      selectAllCatalogueProducts: action,
      clearSelectedProducts: action,
      setEditedPackPrice: action,
      setEditedProductPrice: action,
      setEditedProductLimitedStock: action,
      setEditedVariantQuantity: action,
      setEditedProductVariantQuantity: action,
      setEditedProductQuantity: action,
      clearEditedPrices: action,
      setInventoryPage: action,
      setInventoryLimit: action,
      setCataloguePage: action,
      setCatalogueLimit: action,
      updatePacksDisplayStatus: action,
      updateProductsDisplayStatus: action,
      updatePackVariantQuantitiesLocally: action,
      updateProductsLimitedStockLocally: action,
      updateProductVariantQuantitiesLocally: action,
      calculatePacksDelta: action,
      calculateProductsDelta: action,
      initializeSelectedProducts: action,
      setOrderIdSearchTerm: action,
      setSelectedOrderIds: action,
      clearSelectedOrderIds: action,
      setInventorySortBy: action,
      setInventorySortType: action,
      clearInventorySort: action,
      storeId: observable,
      themeId: observable,
      storeDetailsErrors: observable,
      setStoreId: action,
      setThemeId: action,
      setStoreDetailsErrors: action,
      clearStoreDetailsError: action,
      resetStore: action,
      clearSettings: action,
      createSwagStore: action,
      saveThemeDetails: action,
      updateProductMappingAPI: action,
      getAllStores: action,
      updateStoreStatus: action,
      setStoreListingPage: action,
      setStoreListingLimit: action,
      setStoreListingSearchText: action,
      openAddBalanceModal: action,
      closeAddBalanceModal: action,
      fetchAddBalanceModalWallet: action,
      addWalletBalance: action,
      applyWalletBalanceToStores: action,
      openWalletHistoryDrawer: action,
      closeWalletHistoryDrawer: action,
      fetchWalletTransactions: action,
      setWalletTransactionsPage: action,
      refreshWalletTransactionsIfOpen: action,
      fetchAuthenticationMethods: action,
      setSelectedAuthenticationMethods: action,
      toggleAuthenticationMethod: action,
    });
  }

  // UI Actions
  setSelectedItem = (item: SelectedItem) => {
    this.selectedItem = item;
  };

  private parseThemeValue(value: any) {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      // for images as images
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        try {
          return JSON.parse(trimmed);
        } catch (error) {
          return value;
        }
      }
    }

    return value;
  }

  private normalizeThemeDetails(themeDetails: any): Record<string, any> {
    if (!themeDetails) {
      return {};
    }

    const normalized: Record<string, any> = {};

    if (Array.isArray(themeDetails)) {
      themeDetails.forEach((detail) => {
        const key = detail?.key_name || detail?.key || detail?.name;
        if (!key) return;
        normalized[key] = this.parseThemeValue(detail?.value);
      });
    } else if (typeof themeDetails === "object") {
      Object.entries(themeDetails).forEach(([key, value]) => {
        normalized[key] = this.parseThemeValue(value);
      });
    }

    return normalized;
  }

  private applyThemeDetails(themeDetails: any) {
    const normalized = this.normalizeThemeDetails(themeDetails);
    if (!Object.keys(normalized).length) {
      return;
    }

    const brandingUpdates: Partial<SwagstoreSettingsModel["branding"]> = {};
    const loginUpdates: Partial<SwagstoreSettingsModel["loginPage"]> = {};

    const bannerValue = normalized.company_banners;
    if (bannerValue) {
      let banners = bannerValue;
      if (typeof banners === "string") {
        try {
          banners = JSON.parse(banners);
        } catch {
          banners = [banners];
        }
      }

      if (Array.isArray(banners)) {
        const carouselStrings = banners.filter(
          (image): image is string => typeof image === "string",
        );
        if (carouselStrings.length > 0) {
          brandingUpdates.carouselImages = carouselStrings;
        }
      }
    }

    // Only apply logo if it's a non-empty string (filter out empty strings from backend)
    if (
      typeof normalized.company_logo === "string" &&
      normalized.company_logo.trim() !== ""
    ) {
      brandingUpdates.logo = [normalized.company_logo];
    }

    // Only apply favicon if it's a non-empty string (filter out empty strings from backend)
    if (
      typeof normalized.favicon_url === "string" &&
      normalized.favicon_url.trim() !== ""
    ) {
      brandingUpdates.favicon = [normalized.favicon_url];
    }

    // Only apply color/text fields if they're non-empty strings (filter out empty strings from backend)
    if (
      typeof normalized.app_button_color === "string" &&
      normalized.app_button_color.trim() !== ""
    ) {
      brandingUpdates.primaryColor = normalized.app_button_color;
    }

    if (
      typeof normalized.app_hover_button_color === "string" &&
      normalized.app_hover_button_color.trim() !== ""
    ) {
      brandingUpdates.secondaryColor = normalized.app_hover_button_color;
    }

    if (
      typeof normalized.store_background_color === "string" &&
      normalized.store_background_color.trim() !== ""
    ) {
      brandingUpdates.storeBackgroundColor = normalized.store_background_color;
    }

    if (
      typeof normalized.active_textfield_color === "string" &&
      normalized.active_textfield_color.trim() !== ""
    ) {
      brandingUpdates.textFieldActiveColor = normalized.active_textfield_color;
    }

    if (
      typeof normalized.disabled_textfield_color === "string" &&
      normalized.disabled_textfield_color.trim() !== ""
    ) {
      brandingUpdates.textFieldDisabledColor =
        normalized.disabled_textfield_color;
    }

    if (
      typeof normalized.homepage_product_card_background === "string" &&
      normalized.homepage_product_card_background.trim() !== ""
    ) {
      brandingUpdates.homepageProductCardBackground =
        normalized.homepage_product_card_background;
    }

    if (
      typeof normalized.homepage_product_name_background === "string" &&
      normalized.homepage_product_name_background.trim() !== ""
    ) {
      brandingUpdates.homepageProductNameBackground =
        normalized.homepage_product_name_background;
    }

    if (
      typeof normalized.homepage_product_heading === "string" &&
      normalized.homepage_product_heading.trim() !== ""
    ) {
      brandingUpdates.productHeadingText = normalized.homepage_product_heading;
    }

    if (
      typeof normalized.footer_text === "string" &&
      normalized.footer_text.trim() !== ""
    ) {
      brandingUpdates.footerText = normalized.footer_text;
    }

    if (
      typeof normalized.meta_title === "string" &&
      normalized.meta_title.trim() !== ""
    ) {
      brandingUpdates.metaTitle = normalized.meta_title;
    }

    if (
      typeof normalized.meta_description === "string" &&
      normalized.meta_description.trim() !== ""
    ) {
      brandingUpdates.metaDescription = normalized.meta_description;
    }

    if (normalized.company_name) {
      brandingUpdates.companyName = normalized.company_name;
    }

    // Only apply login page image if it's a non-empty string (filter out empty strings from backend)
    if (
      normalized.login_page_image_url &&
      typeof normalized.login_page_image_url === "string" &&
      normalized.login_page_image_url.trim() !== ""
    ) {
      loginUpdates.loginPageImage = normalized.login_page_image_url;
    }

    // Only apply text fields if they're non-empty (filter out empty strings from backend)
    if (
      normalized.login_page_heading_text !== undefined &&
      normalized.login_page_heading_text !== null
    ) {
      if (
        typeof normalized.login_page_heading_text === "string" &&
        normalized.login_page_heading_text.trim() !== ""
      ) {
        loginUpdates.headingText = normalized.login_page_heading_text;
      }
    }

    if (
      normalized.login_page_subheading_text !== undefined &&
      normalized.login_page_subheading_text !== null
    ) {
      if (
        typeof normalized.login_page_subheading_text === "string" &&
        normalized.login_page_subheading_text.trim() !== ""
      ) {
        loginUpdates.subheadingText = normalized.login_page_subheading_text;
      }
    }

    if (Object.keys(brandingUpdates).length > 0) {
      this.updateBranding(brandingUpdates);
    }

    if (Object.keys(loginUpdates).length > 0) {
      this.updateLoginPage(loginUpdates);
    }
  }

  setDeviceView = (view: DeviceView) => {
    this.deviceView = view;
  };

  setLeftDrawerOpen = (open: boolean) => {
    this.leftDrawerOpen = open;
  };

  setRightDrawerOpen = (open: boolean) => {
    this.rightDrawerOpen = open;
  };

  // Settings Actions
  updateStoreDetails = (
    details: Partial<SwagstoreSettingsModel["storeDetails"]>,
  ) => {
    runInAction(() => {
      this.settings.storeDetails = {
        ...this.settings.storeDetails,
        ...details,
      };
    });
  };

  updateBranding = (branding: Partial<SwagstoreSettingsModel["branding"]>) => {
    runInAction(() => {
      this.settings.branding = { ...this.settings.branding, ...branding };
    });
  };

  updateLoginPage = (
    loginPage: Partial<SwagstoreSettingsModel["loginPage"]>,
  ) => {
    runInAction(() => {
      this.settings.loginPage = { ...this.settings.loginPage, ...loginPage };
    });
  };

  updateProductMapping = (
    productMapping: Partial<SwagstoreSettingsModel["productMapping"]>,
  ) => {
    runInAction(() => {
      this.settings.productMapping = {
        ...this.settings.productMapping,
        ...productMapping,
      };
    });
  };

  // Product Settings Actions
  setProductSearchTerm = (term: string) => {
    this.productSearchTerm = term;
  };

  setSelectedCategory = (category: string) => {
    this.selectedCategory = category;
  };

  setSelectedProductId = (id: number | null) => {
    this.selectedProductId = id;
  };

  setModalOpen = (open: boolean) => {
    this.modalOpen = open;
  };

  setInventorySearchTerm = (term: string) => {
    this.inventorySearchTerm = term;
  };

  setCatalogueSearchTerm = (term: string) => {
    this.catalogueSearchTerm = term;
  };

  setOrderIdSearchTerm = (term: string) => {
    this.orderIdSearchTerm = term;
  };

  setSelectedOrderIds = (orderIds: FilterValue[]) => {
    this.selectedOrderIds = orderIds;
  };

  clearSelectedOrderIds = () => {
    this.selectedOrderIds = [];
    this.orderIdSearchTerm = "";
  };

  setInventorySortBy = (sortBy: string | null) => {
    this.inventorySortBy = sortBy;
  };

  setInventorySortType = (sortType: SortingOptions | null) => {
    this.inventorySortType = sortType;
  };

  clearInventorySort = () => {
    this.inventorySortBy = "quantity";
    this.inventorySortType = SortingOptions.DESC;
  };

  setSelectedCatalogueCategory = (category: string) => {
    this.selectedCatalogueCategory = category;
  };

  setSelectedCatalogueCategoryIds = (categoryIds: number[]) => {
    runInAction(() => {
      this.selectedCatalogueCategoryIds = categoryIds;
    });
  };

  clearSelectedCatalogueCategoryIds = () => {
    this.selectedCatalogueCategoryIds = [];
  };

  setSelectedSource = (source: "inventory" | "catalogue" | "csv") => {
    this.selectedSource = source;
  };

  setDragOver = (over: boolean) => {
    this.dragOver = over;
  };

  setInventoryProducts = (products: Swagpack[]) => {
    this.inventoryProducts = products;
  };

  setStoreId = (id: number | string) => {
    this.storeId = id == null ? null : String(id);
  };

  setThemeId = (id: number) => {
    this.themeId = id;
  };

  resetStore = () => {
    this.storeId = null;
    this.themeId = null;
    this.settings = createEmptySwagstoreSettings();
    this.selectedItem = SWAGSTORE_STEPS.STORE_DETAILS;

    // Clear all product-related data
    this.productsToDisplayOnProductPage = [];
    this.baseProductsToDisplay = [];
    this.fetchedInventoryProducts = [];
    this.fetchedCatalogueProducts = [];
    this.hasLoadedCatalogue = false;
    this.catalogueLoadedForKey = null;
    this.mockupsCatalogueProducts = [];
    this.loadingMockupsCatalogue = false;
    this.mockupsCatalogueHydratedKey = null;
    this.mockupsCataloguePageCache = {};
    this.mockupsCatalogueCountsHydratedKey = null;
    this.mockupsCatalogueHasNextPage = false;
    this.mockupsCatalogueTotalCount = 0;
    this.mockupsCatalogueTotalPages = 0;
    this.mockupsCatalogueStatusCounts = {
      all: 0,
      pending: 0,
      changes_requested: 0,
      approved: 0,
      not_uploaded: 0,
      deleted: 0,
    };
    this.uploadedMockupLogos = [];
    this.loadingUploadedMockupLogos = false;
    this.mockupLogosLoadedForKey = null;
    this.categories = [];
    this.inventoryProducts = [];

    // Clear product selection and filters
    this.selectedInventoryProducts = [];
    this.selectedCatalogueProducts = [];
    this.selectedCategory = "All";
    this.selectedCatalogueCategory = "All";
    this.selectedCatalogueCategoryIds = [];
    this.productSearchTerm = "";
    this.inventorySearchTerm = "";
    this.catalogueSearchTerm = "";
    this.selectedProductId = null;

    // Clear edited prices and quantities
    this.editedPackPrices = {};
    this.editedProductPrices = {};
    this.editedProductLimitedStock = {};
    this.editedVariantQuantities = {};
    this.editedProductVariantQuantities = {};
    this.editedProductQuantities = {};

    this.selectedAuthenticationMethods = [];

    // Reset pagination
    this.inventoryPage = 1;
    this.cataloguePage = 1;
    this.inventoryTotalPages = 0;
    this.inventoryTotalCount = 0;
    this.catalogueTotalPages = 0;
    this.catalogueTotalCount = 0;

    // Clear sorting
    this.inventorySortBy = "quantity";
    this.inventorySortType = SortingOptions.ASC;

    // Clear other state
    this.selectedOrderIds = [];
    this.orderIdSearchTerm = "";
    this.storeDetailsErrors = {};
  };

  clearSettings = () => {
    this.settings = createEmptySwagstoreSettings();
    this.selectedAuthenticationMethods = [];
  };

  setStoreDetailsErrors = (errors: Record<string, string>) => {
    this.storeDetailsErrors = errors;
  };

  clearStoreDetailsError = (key: string) => {
    if (this.storeDetailsErrors[key]) {
      const next = { ...this.storeDetailsErrors };
      delete next[key];
      this.storeDetailsErrors = next;
    }
  };

  validateStoreDetails = () => {
    const errors: Record<string, string> = {};
    const { storeDetails } = this.settings;

    if (!storeDetails.store_name?.trim()) {
      errors.store_name = "Store name is required";
    }

    if (!storeDetails.customer_id) {
      errors.customer_id = "Company name is required";
    }

    // Normalize is_public to boolean (handle 0/1 from backend)
    // Helper to convert any value to boolean
    const normalizeToBoolean = (value: any): boolean => {
      if (value === undefined || value === null) return true; // default to public
      if (value === false || value === 0 || value === "0") return false;
      return true; // true, 1, "1", or any truthy value
    };

    const isPublic = normalizeToBoolean(storeDetails.is_public);

    // Default to public (true) if not set, otherwise normalize
    if (
      storeDetails.is_public === undefined ||
      storeDetails.is_public === null
    ) {
      storeDetails.is_public = true;
    } else {
      // Normalize the value to boolean
      storeDetails.is_public = isPublic;
    }

    // If store is private (is_public = false or 0), domain is required
    if (!isPublic) {
      const hasValidDomain =
        storeDetails.domains &&
        storeDetails.domains.length > 0 &&
        storeDetails.domains.some(
          (domain) => domain?.domain && domain.domain.trim() !== "",
        );

      if (!hasValidDomain) {
        errors.domains = "Domain is required for private store";
      }
    }

    return errors;
  };

  getStoreDetails = async (): Promise<APIResponse> => {
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required");
      }
      if (!this.themeId) {
        throw new Error("Theme ID is required");
      }
      const response = await this.swagStoreAPI.fetchStoreAndThemeDetails(
        this.storeId,
        this.themeId,
      );
      if (response?.result) {
        const { store_details, theme_details } = response.result;

        if (store_details) {
          runInAction(() => {
            // Normalize is_public from backend (0/1 to boolean)
            const normalizedIsPublic =
              store_details.is_public === 1 ||
              store_details.is_public === true ||
              store_details.is_public === "1";

            let authenticationMethodIds: number[] = [];
            if (store_details.authentication_methods) {
              if (Array.isArray(store_details.authentication_methods)) {
                authenticationMethodIds = store_details.authentication_methods
                  .map((method: any) =>
                    typeof method === "object" && method.id
                      ? method.id
                      : typeof method === "number"
                        ? method
                        : null,
                  )
                  .filter((id: any) => id !== null && typeof id === "number");
              }
            } else if (store_details.authentication_method_ids) {
              // Fallback: use authentication_method_ids if authentication_methods is not present
              authenticationMethodIds = Array.isArray(
                store_details.authentication_method_ids,
              )
                ? store_details.authentication_method_ids
                : [];
            }

            const adminPocsSource =
              store_details.admin_pocs ||
              store_details.admin_poc_users ||
              store_details.admins ||
              [];
            const adminPocs = Array.isArray(adminPocsSource)
              ? adminPocsSource
                  .map((admin: any) => {
                    const id = Number(admin?.id || admin?.admin_id || 0);
                    if (!id) return null;
                    const firstName = admin?.first_name || "";
                    const lastName = admin?.last_name || "";
                    const derivedName = `${firstName} ${lastName}`.trim();
                    return {
                      id,
                      name:
                        admin?.name ||
                        admin?.label ||
                        derivedName ||
                        admin?.email ||
                        `Admin ${id}`,
                      email: admin?.email || undefined,
                    };
                  })
                  .filter(Boolean)
              : [];
            const adminPocIds = Array.isArray(store_details.admin_poc_ids)
              ? store_details.admin_poc_ids
              : adminPocs.map((admin: any) => admin.id);
            const normalizedAdminPocs =
              adminPocs.length > 0
                ? adminPocs
                : adminPocIds.map((id: number) => ({
                    id,
                    name: `Admin ${id}`,
                  }));

            this.settings.storeDetails = {
              ...this.settings.storeDetails,
              store_name: store_details.store_name,
              domains: store_details.domains,
              customer_id: store_details.customer_id,
              customer_name: store_details.customer_name,
              is_public: normalizedIsPublic,
              store_id: store_details.store_id,
              authentication_method_ids: authenticationMethodIds,
              admin_poc_ids: adminPocIds,
              admin_pocs: normalizedAdminPocs,
            };

            // Set selected authentication methods to match storeDetails
            this.selectedAuthenticationMethods = authenticationMethodIds;
          });
        }

        if (theme_details) {
          this.applyThemeDetails(theme_details);
        }

        // Prefill markup and discount percentages from store_details if present
        if (store_details) {
          const markupPct = store_details.markup_percentage;
          const discountPct = store_details.discount_percentage;
          if (
            markupPct !== undefined &&
            markupPct !== null &&
            !isNaN(Number(markupPct))
          ) {
            this.updateProductMapping({
              markupPercentage: Number(markupPct),
            });
          }
          if (
            discountPct !== undefined &&
            discountPct !== null &&
            !isNaN(Number(discountPct))
          ) {
            this.updateProductMapping({
              discountPercentage: Number(discountPct),
            });
          }
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  // API Actions
  createSwagStore = async (
    storeData: SwagstoreRequestBody,
  ): Promise<APIResponse> => {
    try {
      const response = await this.swagStoreAPI.createSwagStore(
        storeData,
        this.storeId ?? undefined,
      );
      if (response?.result) {
        if (response.result.store_id) {
          this.setStoreId(response.result.store_id);
        }
        if (response.result.theme_id) {
          this.setThemeId(response.result.theme_id);
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  saveThemeDetails = async (
    themeData: Partial<ThemeRequestBody>,
  ): Promise<APIResponse> => {
    try {
      if (!this.storeId || !this.themeId) {
        throw new Error("Store ID and Theme ID are required");
      }
      const requestData: Partial<ThemeRequestBody> = {
        ...themeData,
        store_id: this.storeId,
        theme_id: this.themeId,
      };
      const response = await this.swagStoreAPI.saveThemeDetails(requestData);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getCustomerInventory = async (): Promise<SwagstoreProductCategory[]> => {
    try {
      if (!this.settings.storeDetails.customer_id) {
        throw new Error("Customer ID is required");
      }
      const response = await this.swagStoreAPI.getCustomerInventory(
        this.settings.storeDetails.customer_id,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateProductMappingAPI = async (
    productMappingData: ProductMappingRequestBody,
  ): Promise<APIResponse> => {
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required for update");
      }
      const response = await this.swagStoreAPI.updateProductMapping(
        this.storeId,
        productMappingData,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  setFetchedInventoryProducts = (products: Swagpack[]) => {
    this.fetchedInventoryProducts = products;
    this.setProductsToDisplayOnProductPage();
  };

  setFetchedCatalogueProducts = (products: Swagpack[]) => {
    this.fetchedCatalogueProducts = products;
    this.setProductsToDisplayOnProductPage();
  };

  setLoadingInventory = (loading: boolean) => {
    this.loadingInventory = loading;
  };

  setLoadingCatalogue = (loading: boolean) => {
    this.loadingCatalogue = loading;
  };

  setHasLoadedCatalogue = (loaded: boolean) => {
    this.hasLoadedCatalogue = loaded;
  };

  setCatalogueLoadedForKey = (key: string | null) => {
    this.catalogueLoadedForKey = key;
  };

  setUploadedMockupLogos = (logos: Array<{ id: number; logo_url: string }>) => {
    this.uploadedMockupLogos = logos;
  };

  setLoadingUploadedMockupLogos = (loading: boolean) => {
    this.loadingUploadedMockupLogos = loading;
  };

  setMockupLogosLoadedForKey = (key: string | null) => {
    this.mockupLogosLoadedForKey = key;
  };

  loadUploadedMockupLogos = async (force = false): Promise<void> => {
    if (!this.storeId || !this.settings.storeDetails.customer_id) return;
    const nextKey = `${this.storeId}:${this.settings.storeDetails.customer_id}`;
    if (!force && this.mockupLogosLoadedForKey === nextKey) return;

    if (this.mockupLogosLoadedForKey !== nextKey) {
      this.setUploadedMockupLogos([]);
    }

    this.setLoadingUploadedMockupLogos(true);
    try {
      const logos = await this.swagStoreAPI.getProductMockupLogos({
        store_id: this.storeId ?? undefined,
        customer_id: Number(this.settings.storeDetails.customer_id),
        only_displayed: true,
      });
      runInAction(() => {
        this.uploadedMockupLogos = logos
          .filter((item) => Boolean(item?.id) && Boolean(item?.logo_url))
          .map((item) => ({ id: item.id, logo_url: item.logo_url }));
        this.mockupLogosLoadedForKey = nextKey;
      });
    } finally {
      runInAction(() => {
        this.loadingUploadedMockupLogos = false;
      });
    }
  };

  uploadMockupLogo = async (file: File): Promise<void> => {
    if (!this.settings.storeDetails.customer_id) {
      throw new Error("Customer id not found.");
    }
    const imageAPI = new ImageAPI();
    const logoUrl = await imageAPI.uploadFile(file, "mockup-logos");
    if (!logoUrl) {
      throw new Error("Failed to upload logo.");
    }
    const response = await this.swagStoreAPI.saveProductMockupLogo({
      store_id: this.storeId ?? undefined,
      customer_id: Number(this.settings.storeDetails.customer_id),
      logo_url: logoUrl,
      display: true,
    });
    const createdId = Number(response?.result?.id);
    if (createdId) {
      runInAction(() => {
        this.uploadedMockupLogos = [
          { id: createdId, logo_url: logoUrl },
          ...this.uploadedMockupLogos.filter((logo) => logo.id !== createdId),
        ];
      });
      return;
    }
    await this.loadUploadedMockupLogos(true);
  };

  deleteMockupLogo = async (logoId: number): Promise<void> => {
    if (!this.settings.storeDetails.customer_id) {
      throw new Error("Customer id not found.");
    }
    await this.swagStoreAPI.setProductMockupLogoActive({
      id: logoId,
      active: false,
      store_id: this.storeId ?? undefined,
      customer_id: Number(this.settings.storeDetails.customer_id),
    });
    runInAction(() => {
      this.uploadedMockupLogos = this.uploadedMockupLogos.filter(
        (logo) => logo.id !== logoId,
      );
    });
  };

  getPacks = async (): Promise<APIResponse> => {
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required");
      }
      if (!this.settings.storeDetails.customer_id) {
        throw new Error("Customer ID is required");
      }
      this.setLoadingInventory(true);

      const filters: SwagstoreFilters = {
        customer_id: this.settings.storeDetails.customer_id,
        store_id: this.storeId,
        search: this.inventorySearchTerm ?? undefined,
        sort_by: this.inventorySortBy ?? undefined,
        sort_type: this.inventorySortType ?? undefined,
      };

      const response = await this.swagStoreAPI.getPacks(filters);

      if (response?.result) {
        const packList = Array.isArray(response.result)
          ? response.result
          : response.result.list || [];

        const mappedProducts = packList.map((pack: Swagpack) => ({
          ...pack,
          active: pack.active,
          blocked_quantity: pack.blocked_quantity,
          created_by: pack.created_by,
          created_by_name: pack.created_by_name,
          created_date: pack.created_date,
          description: pack.description,
          id: pack.id,
          inventory_id: pack.inventory_id,
          label: pack.label,
          last_modified_by: pack.last_modified_by,
          modified_date: pack.modified_date,
          pack_image_url: pack.pack_image_url,
          products_count: pack.products_count,
          quantity: pack.quantity,
          used_quantity: pack.used_quantity,
          products: pack.products,
        }));
        this.setFetchedInventoryProducts(mappedProducts);
      }
      this.setLoadingInventory(false);
      return response;
    } catch (error: any) {
      this.setLoadingInventory(false);
      throw error;
    }
  };

  getProducts = async (): Promise<APIResponse> => {
    const requestSeq = this.catalogueRequestSeq + 1;
    this.catalogueRequestSeq = requestSeq;
    this.activeCatalogueRequestSeq = requestSeq;
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required");
      }
      if (!this.settings.storeDetails.customer_id) {
        throw new Error("Customer ID is required");
      }
      const catalogueKey = `${this.storeId}:${this.settings.storeDetails.customer_id}`;
      this.setLoadingCatalogue(true);
      this.setHasLoadedCatalogue(false);
      this.setCatalogueLoadedForKey(null);

      const filters: SwagstoreFilters = {
        customer_id: this.settings.storeDetails.customer_id,
        store_id: this.storeId,
        // Pagination removed from backend API
        // page: this.cataloguePage,
        // limit: this.catalogueLimit,
      };

      // Add search term if provided
      if (this.catalogueSearchTerm) {
        filters.search = this.catalogueSearchTerm;
      }

      // Category filtering is done locally via filteredCatalogueProducts - do not pass category_ids to API

      const response = await this.swagStoreAPI.getProducts(filters);
      if (requestSeq !== this.activeCatalogueRequestSeq) return response;

      if (response?.result) {
        // Support both direct-array and paginated response contracts.
        const productList = Array.isArray(response.result)
          ? response.result
          : response.result.list || [];
        this.setFetchedCatalogueProducts(productList);
        const totalCount = Number(response?.result?.total_count);
        const totalPages = Number(response?.result?.total_pages);
        this.catalogueTotalCount =
          Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : 0;
        this.catalogueTotalPages =
          Number.isFinite(totalPages) && totalPages >= 0 ? totalPages : 0;
      }
      this.setCatalogueLoadedForKey(catalogueKey);
      this.setLoadingCatalogue(false);
      this.setHasLoadedCatalogue(true);
      return response;
    } catch (error: any) {
      if (requestSeq !== this.activeCatalogueRequestSeq) throw error;
      this.setLoadingCatalogue(false);
      this.setHasLoadedCatalogue(false);
      this.setCatalogueLoadedForKey(null);
      throw error;
    }
  };

  resetMockupsCatalogueList = () => {
    this.mockupsCatalogueProducts = [];
    this.mockupsCatalogueHydratedKey = null;
    this.mockupsCataloguePageCache = {};
    this.mockupsCatalogueCountsHydratedKey = null;
    this.mockupsCatalogueHasNextPage = false;
    this.mockupsCatalogueTotalCount = 0;
    this.mockupsCatalogueTotalPages = 0;
    this.mockupsCatalogueStatusCounts = {
      all: 0,
      pending: 0,
      changes_requested: 0,
      approved: 0,
      not_uploaded: 0,
      deleted: 0,
    };
    this.mockupsLocalStatusOverrides = {};
    this.mockupsLocalDeletedProductIds = {};
  };

  markProductAsLocallyDeleted = (productId: number) => {
    if (!Number.isFinite(productId) || productId <= 0) return;
    if (this.mockupsLocalDeletedProductIds[productId]) return;
    this.mockupsLocalDeletedProductIds[productId] = true;
    // Drop stale deleted-tab page cache so switching tabs refetches fresh products.
    Object.keys(this.mockupsCataloguePageCache).forEach((key) => {
      const statusPart = key.split("|")[4];
      if (statusPart === "deleted") {
        delete this.mockupsCataloguePageCache[key];
      }
    });
    this.mockupsCatalogueStatusCounts = {
      ...this.mockupsCatalogueStatusCounts,
      deleted: (this.mockupsCatalogueStatusCounts.deleted ?? 0) + 1,
    };
  };

  private applyMockupLocalOverridesToList = (
    products: Swagpack[],
    tab:
      | "all"
      | "pending"
      | "changes_requested"
      | "approved"
      | "not_uploaded"
      | "deleted",
  ): Swagpack[] => {
    let nextList = [...products];
    const overrides = Object.entries(this.mockupsLocalStatusOverrides);

    overrides.forEach(([productIdKey, override]) => {
      if (tab === "deleted") {
        // Deleted tab is orthogonal to active product status; rely on backend list.
        return;
      }
      const productId = Number(productIdKey);
      if (!Number.isFinite(productId)) return;
      nextList = nextList.filter((p) => p.id !== productId);
      if (tab === "all" || override.status === tab) {
        nextList.unshift(override.product);
      }
    });

    return nextList;
  };

  applyLocalMockupStatusTransition = (params: {
    productId: number;
    nextStatus:
      | "pending"
      | "changes_requested"
      | "approved"
      | "not_uploaded"
      | "deleted";
    currentTab:
      | "all"
      | "pending"
      | "changes_requested"
      | "approved"
      | "not_uploaded"
      | "deleted";
    previousStatus?:
      | "pending"
      | "changes_requested"
      | "approved"
      | "not_uploaded"
      | "deleted";
    incrementMockupCounts?: boolean;
  }) => {
    const {
      productId,
      nextStatus,
      currentTab,
      previousStatus,
      incrementMockupCounts,
    } = params;
    if (!Number.isFinite(productId) || productId <= 0) return;
    const inferStatusFromProduct = (
      product: Swagpack | undefined,
    ):
      | "pending"
      | "changes_requested"
      | "approved"
      | "not_uploaded"
      | "deleted"
      | undefined => {
      if (!product) return undefined;
      const uploaded = Math.max(0, Number(product.mockups_uploaded_count ?? 0));
      const pending = Math.max(0, Number(product.mockups_pending_count ?? 0));
      if (uploaded <= 0) return "not_uploaded";
      if (pending <= 0) return "approved";
      // Without explicit status on the product row, treat non-approved as pending by default.
      return "pending";
    };
    const patchProductLocalMockupCounts = (product: Swagpack): Swagpack => {
      const uploaded = Math.max(0, Number(product.mockups_uploaded_count ?? 0));
      const pending = Math.max(0, Number(product.mockups_pending_count ?? 0));
      if (nextStatus === "not_uploaded") {
        return {
          ...product,
          mockups_uploaded_count: 0,
          mockups_pending_count: 0,
        };
      }
      if (incrementMockupCounts) {
        return {
          ...product,
          mockups_uploaded_count: uploaded + 1,
          mockups_pending_count: pending + 1,
        };
      }
      if (uploaded > 0) {
        if (nextStatus === "approved") {
          return {
            ...product,
            mockups_uploaded_count: uploaded,
            mockups_pending_count: 0,
          };
        }
        if (nextStatus === "pending" || nextStatus === "changes_requested") {
          return {
            ...product,
            mockups_uploaded_count: uploaded,
            mockups_pending_count: Math.max(1, pending),
          };
        }
        return product;
      }
      return {
        ...product,
        mockups_uploaded_count: 1,
        mockups_pending_count:
          nextStatus === "approved" ? 0 : Math.max(1, pending),
      };
    };

    const fromStatus =
      previousStatus ??
      (currentTab === "all"
        ? inferStatusFromProduct(
            this.mockupsCatalogueProducts.find((p) => p.id === productId) ??
              this.mockupsLocalStatusOverrides[productId]?.product,
          )
        : (currentTab as
            | "pending"
            | "changes_requested"
            | "approved"
            | "not_uploaded"
            | "deleted"));

    if (fromStatus && fromStatus !== nextStatus) {
      this.mockupsCatalogueStatusCounts = {
        ...this.mockupsCatalogueStatusCounts,
        [fromStatus]: Math.max(
          0,
          (this.mockupsCatalogueStatusCounts[fromStatus] ?? 0) - 1,
        ),
        [nextStatus]: (this.mockupsCatalogueStatusCounts[nextStatus] ?? 0) + 1,
      };
    }

    const currentProduct = this.mockupsCatalogueProducts.find(
      (p) => p.id === productId,
    );
    if (currentProduct) {
      const patchedProduct = patchProductLocalMockupCounts(currentProduct);
      this.mockupsCatalogueProducts = this.mockupsCatalogueProducts.map((p) =>
        p.id === productId ? patchedProduct : p,
      );
      this.mockupsLocalStatusOverrides[productId] = {
        status: nextStatus,
        product: patchedProduct,
      };
    } else if (this.mockupsLocalStatusOverrides[productId]) {
      const patchedProduct = patchProductLocalMockupCounts(
        this.mockupsLocalStatusOverrides[productId].product,
      );
      this.mockupsLocalStatusOverrides[productId] = {
        ...this.mockupsLocalStatusOverrides[productId],
        status: nextStatus,
        product: patchedProduct,
      };
    }

    if (currentTab !== "all" && currentTab !== nextStatus) {
      this.mockupsCatalogueProducts = this.mockupsCatalogueProducts.filter(
        (p) => p.id !== productId,
      );
      this.mockupsCatalogueTotalCount = Math.max(
        0,
        this.mockupsCatalogueTotalCount - 1,
      );
    }

    const keys = Object.keys(this.mockupsCataloguePageCache);
    keys.forEach((key) => {
      const page = this.mockupsCataloguePageCache[key];
      if (!page) return;
      const statusPart = key.split("|")[4] as
        | "all"
        | "pending"
        | "changes_requested"
        | "approved"
        | "not_uploaded"
        | "deleted"
        | undefined;
      if (statusPart && currentTab !== "all" && statusPart !== currentTab) {
        return;
      }
      this.mockupsCataloguePageCache[key] = {
        ...page,
        products: page.products.filter((p) => p.id !== productId),
      };
    });
  };

  fetchMockupsCataloguePage = async (options: {
    page: number;
    pageSize: number;
    search?: string;
    categoryIds?: number[];
    mockupStatus?:
      | "all"
      | "pending"
      | "changes_requested"
      | "approved"
      | "not_uploaded"
      | "deleted";
  }): Promise<void> => {
    if (!this.storeId) {
      throw new Error("Store ID is required");
    }
    if (!this.settings.storeDetails.customer_id) {
      throw new Error("Customer ID is required");
    }
    const catalogueKey = `${this.storeId}:${this.settings.storeDetails.customer_id}`;
    this.loadingMockupsCatalogue = true;
    try {
      const filters: SwagstoreFilters = {
        customer_id: this.settings.storeDetails.customer_id,
        store_id: this.storeId,
        page: Math.max(1, options.page),
        limit: options.pageSize,
        display_on_store: 1,
        mockup_status: options.mockupStatus ?? "all",
      };
      const q = options.search?.trim();
      if (q) {
        filters.search = q;
      }
      if ((options.categoryIds?.length ?? 0) > 0) {
        filters.category_ids = options.categoryIds;
      }
      const requestCategoryIds = [...(filters.category_ids ?? [])].sort(
        (a, b) => a - b,
      );
      const requestKey = [
        String(this.storeId),
        String(this.settings.storeDetails.customer_id),
        String(filters.page ?? 1),
        String(filters.limit ?? options.pageSize),
        filters.mockup_status ?? "all",
        filters.search ?? "",
        requestCategoryIds.join(","),
        String(filters.display_on_store ?? ""),
      ].join("|");
      const countsCategoryIds = [...(filters.category_ids ?? [])].sort(
        (a, b) => a - b,
      );
      const countsKey = [
        String(this.storeId),
        String(this.settings.storeDetails.customer_id),
        filters.search ?? "",
        countsCategoryIds.join(","),
        String(filters.display_on_store ?? ""),
      ].join("|");
      if (this.mockupsCatalogueCountsHydratedKey !== countsKey) {
        const countsResponse =
          await this.swagStoreAPI.getProductMockupStatusCounts(filters);
        const statusCounts = countsResponse?.result;
        this.mockupsCatalogueStatusCounts = {
          all: Number(statusCounts?.all) || 0,
          pending: Number(statusCounts?.pending) || 0,
          changes_requested: Number(statusCounts?.changes_requested) || 0,
          approved: Number(statusCounts?.approved) || 0,
          not_uploaded: Number(statusCounts?.not_uploaded) || 0,
          deleted: Number(statusCounts?.deleted) || 0,
        };
        this.mockupsCatalogueCountsHydratedKey = countsKey;
      }

      const cachedPage = this.mockupsCataloguePageCache[requestKey];
      if (cachedPage) {
        this.mockupsCatalogueProducts = this.applyMockupLocalOverridesToList(
          cachedPage.products,
          filters.mockup_status ?? "all",
        );
        this.mockupsCatalogueTotalCount = cachedPage.totalCount;
        this.mockupsCatalogueTotalPages = cachedPage.totalPages;
        this.mockupsCatalogueHasNextPage = cachedPage.hasNextPage;
        this.mockupsCatalogueHydratedKey = catalogueKey;
        return;
      }

      const response = await this.swagStoreAPI.getProducts(filters);
      const productList = Array.isArray(response?.result)
        ? response.result
        : response?.result?.list || [];
      const mergedProducts = this.applyMockupLocalOverridesToList(
        productList,
        filters.mockup_status ?? "all",
      );
      this.mockupsCatalogueProducts = mergedProducts;
      const totalCount = Number(response?.result?.total_count);
      const totalPages = Number(response?.result?.total_pages);
      const hasNextPageFromBackend = response?.result?.has_next_page;
      this.mockupsCatalogueTotalCount =
        Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : 0;
      this.mockupsCatalogueTotalPages =
        Number.isFinite(totalPages) && totalPages >= 0 ? totalPages : 0;
      if (typeof hasNextPageFromBackend === "boolean") {
        this.mockupsCatalogueHasNextPage = hasNextPageFromBackend;
      } else if (this.mockupsCatalogueTotalPages > 0) {
        this.mockupsCatalogueHasNextPage =
          Math.max(1, options.page) < this.mockupsCatalogueTotalPages;
      } else {
        this.mockupsCatalogueHasNextPage = false;
      }
      this.mockupsCataloguePageCache[requestKey] = {
        products: mergedProducts,
        totalCount: this.mockupsCatalogueTotalCount,
        totalPages: this.mockupsCatalogueTotalPages,
        hasNextPage: this.mockupsCatalogueHasNextPage,
      };
      this.mockupsCatalogueHydratedKey = catalogueKey;
      if ((filters.mockup_status ?? "all") === "deleted") {
        mergedProducts.forEach((p) => {
          if (p?.id) this.mockupsLocalDeletedProductIds[p.id] = true;
        });
      }
    } catch (e) {
      this.mockupsCatalogueProducts = [];
      this.mockupsCatalogueHasNextPage = false;
      this.mockupsCatalogueTotalCount = 0;
      this.mockupsCatalogueTotalPages = 0;
      this.mockupsCatalogueCountsHydratedKey = null;
      this.mockupsCatalogueStatusCounts = {
        all: 0,
        pending: 0,
        changes_requested: 0,
        approved: 0,
        not_uploaded: 0,
        deleted: 0,
      };
      this.mockupsCatalogueHydratedKey = catalogueKey;
      throw e;
    } finally {
      this.loadingMockupsCatalogue = false;
    }
  };

  clearFetchedProducts = () => {
    this.setFetchedInventoryProducts([]);
    this.setFetchedCatalogueProducts([]);
    this.setLoadingInventory(false);
    this.setLoadingCatalogue(false);
    this.setHasLoadedCatalogue(false);
    this.setCatalogueLoadedForKey(null);
    this.mockupsCatalogueProducts = [];
    this.loadingMockupsCatalogue = false;
    this.mockupsCatalogueHydratedKey = null;
    this.mockupsCataloguePageCache = {};
    this.mockupsCatalogueHasNextPage = false;
    this.mockupsCatalogueTotalCount = 0;
    this.mockupsCatalogueTotalPages = 0;
    this.mockupsCatalogueCountsHydratedKey = null;
    this.mockupsCatalogueStatusCounts = {
      all: 0,
      pending: 0,
      changes_requested: 0,
      approved: 0,
      not_uploaded: 0,
      deleted: 0,
    };
    this.mockupsLocalStatusOverrides = {};
    this.mockupsLocalDeletedProductIds = {};
    this.setUploadedMockupLogos([]);
    this.setLoadingUploadedMockupLogos(false);
    this.setMockupLogosLoadedForKey(null);
  };

  // Product Selection Actions
  toggleInventoryProduct = (productId: number) => {
    if (this.selectedInventoryProducts.includes(productId)) {
      this.selectedInventoryProducts = this.selectedInventoryProducts.filter(
        (id) => id !== productId,
      );
    } else {
      this.selectedInventoryProducts = [
        ...this.selectedInventoryProducts,
        productId,
      ];
    }
  };

  toggleCatalogueProduct = (productId: number) => {
    if (this.selectedCatalogueProducts.includes(productId)) {
      this.selectedCatalogueProducts = this.selectedCatalogueProducts.filter(
        (id) => id !== productId,
      );
    } else {
      this.selectedCatalogueProducts = [
        ...this.selectedCatalogueProducts,
        productId,
      ];
    }
  };

  selectAllInventoryProducts = (productIds: number[]) => {
    if (productIds.length === 0) {
      return;
    }

    const allSelected = productIds.every((id) =>
      this.selectedInventoryProducts.includes(id),
    );

    if (allSelected) {
      // Deselect all visible products (keep others that might be selected)
      this.selectedInventoryProducts = this.selectedInventoryProducts.filter(
        (id) => !productIds.includes(id),
      );
    } else {
      // Select all visible products (merge with existing selections)
      const newSelections = [
        ...this.selectedInventoryProducts,
        ...productIds.filter(
          (id) => !this.selectedInventoryProducts.includes(id),
        ),
      ];
      this.selectedInventoryProducts = newSelections;
    }
  };

  selectAllCatalogueProducts = (productIds: number[]) => {
    if (productIds.length === 0) {
      return;
    }

    const allSelected = productIds.every((id) =>
      this.selectedCatalogueProducts.includes(id),
    );

    if (allSelected) {
      // Deselect all visible products (keep others that might be selected)
      this.selectedCatalogueProducts = this.selectedCatalogueProducts.filter(
        (id) => !productIds.includes(id),
      );
    } else {
      // Select all visible products (merge with existing selections)
      const newSelections = [
        ...this.selectedCatalogueProducts,
        ...productIds.filter(
          (id) => !this.selectedCatalogueProducts.includes(id),
        ),
      ];
      this.selectedCatalogueProducts = newSelections;
    }
  };

  clearSelectedProducts = () => {
    this.selectedInventoryProducts = [];
    this.selectedCatalogueProducts = [];
    this.setInventorySearchTerm("");
    this.setCatalogueSearchTerm("");
    this.clearSelectedOrderIds();
    this.clearSelectedCatalogueCategoryIds();
    this.clearEditedPrices();
  };

  setEditedPackPrice = (packId: number, price: number | null) => {
    this.editedPackPrices[packId] = price;
  };

  setEditedProductPrice = (productId: number, price: number | null) => {
    this.editedProductPrices[productId] = price;
  };

  setEditedVariantQuantity = (
    packId: number,
    variantId: number,
    quantity: number | null,
  ) => {
    if (!this.editedVariantQuantities[packId]) {
      this.editedVariantQuantities[packId] = {};
    }
    this.editedVariantQuantities[packId][variantId] = quantity;
  };

  setEditedProductVariantQuantity = (
    productId: number,
    variantId: number,
    quantity: number | null,
  ) => {
    if (!this.editedProductVariantQuantities[productId]) {
      this.editedProductVariantQuantities[productId] = {};
    }
    this.editedProductVariantQuantities[productId][variantId] = quantity;
  };

  setEditedProductQuantity = (productId: number, quantity: number | null) => {
    this.editedProductQuantities[productId] = quantity;
  };

  clearEditedPrices = () => {
    this.editedPackPrices = {};
    this.editedProductPrices = {};
    this.editedProductLimitedStock = {};
  };

  setEditedProductLimitedStock = (
    productId: number,
    value: boolean | undefined,
  ) => {
    this.editedProductLimitedStock[productId] = value;
  };

  clearEditedQuantities = () => {
    this.editedVariantQuantities = {};
    this.editedProductVariantQuantities = {};
    this.editedProductQuantities = {};
    this.editedProductLimitedStock = {};
  };

  setInventoryPage = (page: number) => {
    this.inventoryPage = page;
  };

  setInventoryLimit = (limit: number) => {
    this.inventoryLimit = limit;
    this.inventoryPage = 1; // Reset to first page when limit changes
  };

  setCataloguePage = (page: number) => {
    this.cataloguePage = page;
  };

  setCatalogueLimit = (limit: number) => {
    this.catalogueLimit = limit;
    this.cataloguePage = 1; // Reset to first page when limit changes
  };

  setProductsToDisplayOnProductPage = () => {
    const combined = [
      ...this.fetchedInventoryProducts,
      ...this.fetchedCatalogueProducts,
    ];
    const productsMap = new Map<number, Swagpack>();

    combined.forEach((pack) => {
      const isEnabled = Boolean(pack.display_on_store);
      if (isEnabled) {
        productsMap.set(pack.id, pack);
      } else if (!isEnabled && productsMap.has(pack.id)) {
        productsMap.delete(pack.id);
      }
    });

    this.productsToDisplayOnProductPage = Array.from(productsMap.values());
  };

  // Set base products list - only called initially or after add/remove operations
  setBaseProductsToDisplay = () => {
    const combined = [
      ...this.fetchedInventoryProducts,
      ...this.fetchedCatalogueProducts,
    ];
    const productsMap = new Map<number, Swagpack>();

    combined.forEach((pack) => {
      const isEnabled = Boolean(pack.display_on_store);
      if (isEnabled) {
        productsMap.set(pack.id, pack);
      } else if (!isEnabled && productsMap.has(pack.id)) {
        productsMap.delete(pack.id);
      }
    });

    this.baseProductsToDisplay = Array.from(productsMap.values());
  };

  calculatePacksDelta = (): Array<{
    pack_id: number;
    pack_price?: number;
    display_on_store?: boolean;
    variants?: Array<{
      variant_id: number;
      product_id: number;
      quantity_for_store: number;
    }>;
  }> => {
    const packs: Array<{
      pack_id: number;
      pack_price?: number;
      display_on_store?: boolean;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }> = [];

    const applyPackCostFloor = (pack: Swagpack, price: number): number => {
      const floor =
        pack.pack_cost_price != null &&
        !Number.isNaN(Number(pack.pack_cost_price))
          ? Number(pack.pack_cost_price)
          : undefined;
      if (floor === undefined) return price;
      return Math.max(price, floor);
    };

    const getChangedPackVariants = (
      pack: Swagpack,
    ): Array<{
      variant_id: number;
      product_id: number;
      quantity_for_store: number;
    }> => {
      const changedVariants: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }> = [];

      if (pack.products) {
        pack.products.forEach((product) => {
          if (product.variants) {
            product.variants.forEach((variant) => {
              const vid = variant.variant_id ?? variant.id;
              if (vid && variant.product_id) {
                const editedQuantity =
                  this.editedVariantQuantities[pack.id]?.[vid];

                if (editedQuantity !== undefined && editedQuantity !== null) {
                  changedVariants.push({
                    variant_id: vid,
                    product_id: variant.product_id,
                    quantity_for_store: editedQuantity,
                  });
                }
              }
            });
          }
        });
      }

      return changedVariants;
    };

    const hasVariantChanges = (packId: number): boolean => {
      const pack = this.fetchedInventoryProducts.find((p) => p.id === packId);
      if (!pack || !pack.products) return false;

      return pack.products.some((product) => {
        if (!product.variants) return false;
        return product.variants.some((variant) => {
          const vid = variant.variant_id ?? variant.id;
          if (!vid) return false;
          const editedQuantity = this.editedVariantQuantities[packId]?.[vid];
          return editedQuantity !== undefined && editedQuantity !== null;
        });
      });
    };

    this.selectedInventoryProducts.forEach((packId) => {
      const pack = this.fetchedInventoryProducts.find((p) => p.id === packId);
      if (!pack) return;

      const packItem: {
        pack_id: number;
        pack_price?: number;
        display_on_store?: boolean;
        variants?: Array<{
          variant_id: number;
          product_id: number;
          quantity_for_store: number;
        }>;
      } = { pack_id: packId };

      const isNewPack = !pack.display_on_store;

      let priceChanged = false;
      if (this.editedPackPrices[packId] !== undefined) {
        const editedPrice = this.editedPackPrices[packId];
        const currentPrice = pack.pack_price ?? 0;
        const finalPrice = editedPrice !== null ? editedPrice : 0;
        priceChanged = finalPrice !== currentPrice;
      }

      const variantChanged = hasVariantChanges(packId);

      if (isNewPack) {
        packItem.display_on_store = true;
      }

      if (priceChanged || isNewPack) {
        const editedPrice = this.editedPackPrices[packId];
        const rawPrice =
          editedPrice !== undefined && editedPrice !== null
            ? editedPrice
            : (pack.pack_price ?? 0);
        packItem.pack_price = applyPackCostFloor(pack, rawPrice);
      }

      if (variantChanged) {
        const changedVariants = getChangedPackVariants(pack);
        if (changedVariants.length > 0) {
          packItem.variants = changedVariants;
        }
      }

      if (isNewPack || priceChanged || variantChanged) {
        packs.push(packItem);
      }
    });

    this.fetchedInventoryProducts.forEach((pack) => {
      if (
        pack.display_on_store &&
        !this.selectedInventoryProducts.includes(pack.id)
      ) {
        packs.push({
          pack_id: pack.id,
          display_on_store: false,
        });
      }
    });

    return packs;
  };

  calculateProductsDelta = (): Array<{
    product_id: number;
    selling_price?: number;
    display_on_store?: boolean;
    limited_stock?: boolean;
    variants?: Array<{
      variant_id: number;
      product_id: number;
      quantity_for_store: number;
    }>;
  }> => {
    const products: Array<{
      product_id: number;
      selling_price?: number;
      display_on_store?: boolean;
      limited_stock?: boolean;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }> = [];

    const applyPackCostFloor = (pack: Swagpack, price: number): number => {
      const floor =
        pack.pack_cost_price != null &&
        !Number.isNaN(Number(pack.pack_cost_price))
          ? Number(pack.pack_cost_price)
          : undefined;
      if (floor === undefined) return price;
      return Math.max(price, floor);
    };

    const getChangedProductVariants = (
      product: Swagpack,
    ): Array<{
      variant_id: number;
      product_id: number;
      quantity_for_store: number;
    }> => {
      const changedVariants: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }> = [];

      if (product.products) {
        product.products.forEach((prod) => {
          if (prod.variants) {
            prod.variants.forEach((variant) => {
              const vid = variant.variant_id ?? variant.id;
              if (vid && variant.product_id) {
                const editedQuantity =
                  this.editedProductVariantQuantities[product.id]?.[vid];

                if (editedQuantity !== undefined && editedQuantity !== null) {
                  changedVariants.push({
                    variant_id: vid,
                    product_id: variant.product_id,
                    quantity_for_store: editedQuantity,
                  });
                }
              }
            });
          }
        });
      }

      return changedVariants;
    };

    const hasVariantChanges = (productId: number): boolean => {
      const product = this.fetchedCatalogueProducts.find(
        (p) => p.id === productId,
      );
      if (!product || !product.products) return false;

      return product.products.some((prod) => {
        if (!prod.variants) return false;
        return prod.variants.some((variant) => {
          const vid = variant.variant_id ?? variant.id;
          if (!vid) return false;
          const editedQuantity =
            this.editedProductVariantQuantities[productId]?.[vid];
          return editedQuantity !== undefined && editedQuantity !== null;
        });
      });
    };

    this.selectedCatalogueProducts.forEach((productId) => {
      const product = this.fetchedCatalogueProducts.find(
        (p) => p.id === productId,
      );
      if (!product) return;

      const productItem: {
        product_id: number;
        selling_price?: number;
        display_on_store?: boolean;
        limited_stock?: boolean;
        variants?: Array<{
          variant_id: number;
          product_id: number;
          quantity_for_store: number;
        }>;
      } = { product_id: productId };

      const isNewProduct = !product.display_on_store;

      const editedLimitedStock = this.editedProductLimitedStock[productId];
      const currentLimitedStock = Boolean(product.limited_stock);
      const finalLimitedStock =
        editedLimitedStock !== undefined
          ? editedLimitedStock
          : currentLimitedStock;
      const limitedStockChanged = finalLimitedStock !== currentLimitedStock;

      let priceChanged = false;
      if (this.editedProductPrices[productId] !== undefined) {
        const editedPrice = this.editedProductPrices[productId];
        const currentPrice = product.pack_price ?? 0;
        const finalPrice = editedPrice !== null ? editedPrice : 0;
        priceChanged = finalPrice !== currentPrice;
      }

      const variantChanged = hasVariantChanges(productId);

      if (isNewProduct) {
        productItem.display_on_store = true;
      }

      if (priceChanged || isNewProduct) {
        const editedPrice = this.editedProductPrices[productId];
        const rawPrice =
          editedPrice !== undefined && editedPrice !== null
            ? editedPrice
            : (product.pack_price ?? 0);
        productItem.selling_price = applyPackCostFloor(product, rawPrice);
      }

      if (limitedStockChanged || isNewProduct) {
        productItem.limited_stock = finalLimitedStock;
      }

      if (variantChanged || isNewProduct) {
        if (isNewProduct) {
          const allVariants: Array<{
            variant_id: number;
            product_id: number;
            quantity_for_store: number;
          }> = [];

          if (product.products) {
            product.products.forEach((prod) => {
              if (prod.variants) {
                prod.variants.forEach((variant) => {
                  const vid = variant.variant_id ?? variant.id;
                  if (vid && variant.product_id) {
                    const editedQuantity =
                      this.editedProductVariantQuantities[product.id]?.[vid];
                    const quantity =
                      editedQuantity !== undefined && editedQuantity !== null
                        ? editedQuantity
                        : (variant.quantity_for_store ?? 0);

                    allVariants.push({
                      variant_id: vid,
                      product_id: variant.product_id,
                      quantity_for_store: quantity,
                    });
                  }
                });
              }
            });
          }

          if (allVariants.length > 0) {
            productItem.variants = allVariants;
          }
        } else {
          const changedVariants = getChangedProductVariants(product);
          if (changedVariants.length > 0) {
            productItem.variants = changedVariants;
          }
        }
      }

      if (
        isNewProduct ||
        priceChanged ||
        variantChanged ||
        limitedStockChanged
      ) {
        products.push(productItem);
      }
    });

    this.fetchedCatalogueProducts.forEach((product) => {
      if (
        product.display_on_store &&
        !this.selectedCatalogueProducts.includes(product.id)
      ) {
        products.push({
          product_id: product.id,
          display_on_store: false,
        });
      }
    });

    return products;
  };

  updatePacksDisplayStatus = (add: number[], remove: number[]) => {
    runInAction(() => {
      this.fetchedInventoryProducts = this.fetchedInventoryProducts.map(
        (pack) => {
          if (add.includes(pack.id)) {
            return { ...pack, display_on_store: true };
          }
          if (remove.includes(pack.id)) {
            return { ...pack, display_on_store: false };
          }
          return pack;
        },
      );
    });

    this.setProductsToDisplayOnProductPage();
    // Update base products list after add/remove
    this.setBaseProductsToDisplay();
  };

  updateProductsDisplayStatus = (add: number[], remove: number[]) => {
    runInAction(() => {
      this.fetchedCatalogueProducts = this.fetchedCatalogueProducts.map(
        (pack) => {
          if (add.includes(pack.id)) {
            return { ...pack, display_on_store: true };
          }
          if (remove.includes(pack.id)) {
            return {
              ...pack,
              display_on_store: false,
              limited_stock: false,
            };
          }
          return pack;
        },
      );
    });

    this.setProductsToDisplayOnProductPage();
    // Update base products list after add/remove
    this.setBaseProductsToDisplay();
  };

  updatePackVariantQuantitiesLocally = (
    packsDelta: Array<{
      pack_id: number;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }>,
  ) => {
    runInAction(() => {
      this.fetchedInventoryProducts = this.fetchedInventoryProducts.map(
        (pack) => {
          const packDelta = packsDelta.find((p) => p.pack_id === pack.id);
          if (
            !packDelta ||
            !packDelta.variants ||
            packDelta.variants.length === 0
          ) {
            return pack;
          }
          // Update pack with new variant quantities
          const updatedPack = { ...pack };
          if (updatedPack.products) {
            updatedPack.products = updatedPack.products.map((product) => {
              if (!product.variants) return product;

              const updatedProduct = { ...product };
              updatedProduct.variants = product.variants.map((variant) => {
                const vid = variant.variant_id ?? variant.id;
                if (!vid) return variant;

                const variantDelta = packDelta.variants!.find(
                  (v) => v.variant_id === vid,
                );

                if (variantDelta) {
                  const originalQuantity = variant.quantity_for_store ?? 0;
                  const newQuantity =
                    originalQuantity + variantDelta.quantity_for_store;
                  const quantity = variant.quantity
                    ? variant.quantity - variantDelta.quantity_for_store
                    : 0;

                  return {
                    ...variant,
                    quantity_for_store: newQuantity,
                    quantity: quantity,
                  };
                }

                return variant;
              });

              return updatedProduct;
            });

            // Recalculate pack's available_quantity: quantity - sum(variant.quantity_for_store) + used_quantity
            let totalQuantityForStore = 0;
            updatedPack.products.forEach((product) => {
              if (product.variants) {
                product.variants.forEach((variant) => {
                  totalQuantityForStore += variant.quantity_for_store ?? 0;
                });
              }
            });
            // Formula: available_quantity = initial_quantity - quantity_for_store + used_quantity
            const packQuantity = updatedPack.quantity ?? 0;
            const packUsedQuantity = updatedPack.used_quantity ?? 0;
            updatedPack.available_quantity =
              packQuantity - (totalQuantityForStore + packUsedQuantity);
          }

          return updatedPack;
        },
      );
    });
  };

  updateProductsLimitedStockLocally = (
    productsDelta: Array<{
      product_id: number;
      limited_stock?: boolean;
    }>,
  ) => {
    runInAction(() => {
      this.fetchedCatalogueProducts = this.fetchedCatalogueProducts.map(
        (product) => {
          const productDelta = productsDelta.find(
            (p) => p.product_id === product.id,
          );
          if (!productDelta || productDelta.limited_stock === undefined) {
            return product;
          }
          return { ...product, limited_stock: productDelta.limited_stock };
        },
      );
    });
  };

  updateProductVariantQuantitiesLocally = (
    productsDelta: Array<{
      product_id: number;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }>,
  ) => {
    runInAction(() => {
      this.fetchedCatalogueProducts = this.fetchedCatalogueProducts.map(
        (product) => {
          const productDelta = productsDelta.find(
            (p) => p.product_id === product.id,
          );
          if (
            !productDelta ||
            !productDelta.variants ||
            productDelta.variants.length === 0
          ) {
            return product;
          }

          // Update product with new variant quantities
          const updatedProduct = { ...product };
          if (updatedProduct.products) {
            updatedProduct.products = updatedProduct.products.map((prod) => {
              if (!prod.variants) return prod;

              const updatedProd = { ...prod };
              updatedProd.variants = prod.variants.map((variant) => {
                const vid = variant.variant_id ?? variant.id;
                if (!vid) return variant;

                const variantDelta = productDelta.variants!.find(
                  (v) => v.variant_id === vid,
                );

                if (variantDelta) {
                  // Backend adds the value, so new = original + sent value
                  const originalQuantity = variant.quantity_for_store ?? 0;
                  const newQuantity =
                    originalQuantity + variantDelta.quantity_for_store;

                  return {
                    ...variant,
                    quantity_for_store: newQuantity,
                  };
                }

                return variant;
              });

              return updatedProd;
            });
          }

          return updatedProduct;
        },
      );
    });
  };

  initializeSelectedProducts = () => {
    runInAction(() => {
      this.selectedInventoryProducts = this.fetchedInventoryProducts
        .filter((pack) => pack.display_on_store)
        .map((pack) => pack.id);

      this.selectedCatalogueProducts = this.fetchedCatalogueProducts
        .filter((product) => product.display_on_store)
        .map((product) => product.id);
    });
  };

  downloadCatalogueTemplate = async (): Promise<APIBlobResponse> => {
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required");
      }
      const response = await this.swagStoreAPI.downloadCatalogueTemplate(
        this.storeId,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  uploadProductsCSV = async (file: File): Promise<APIResponse> => {
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required");
      }
      const response = await this.swagStoreAPI.uploadProductsCSV(
        this.storeId,
        file,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getCategories = async (): Promise<void> => {
    try {
      const categories = await this.swagStoreAPI.fetchCategories();
      runInAction(() => {
        this.categories = categories;
      });
    } catch (error: any) {
      throw error;
    }
  };

  downloadProductsPacksCSV = async (): Promise<APIBlobResponse> => {
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required");
      }
      const response = await this.swagStoreAPI.downloadProductsPacksCSV(
        this.storeId,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  // Store Listing Actions
  setStoreListingPage = (page: number) => {
    this.storeListingPage = page;
  };

  setStoreListingLimit = (limit: number) => {
    this.storeListingLimit = limit;
    this.storeListingPage = 1;
  };

  setStoreListingSearchText = (searchText: string) => {
    this.storeListingSearchText = searchText;
  };

  openAddBalanceModal = (storeId: number | string) => {
    this.addBalanceStoreId = storeId;
    this.fetchAddBalanceModalWallet(storeId).catch(() => {});
  };

  closeAddBalanceModal = () => {
    this.addBalanceStoreId = null;
    this.addBalanceWalletLoading = false;
    this.addBalanceSubmitting = false;
    this.addBalanceCurrentBalance = null;
    this.addBalanceWalletError = null;
  };

  openWalletHistoryDrawer = (storeId: number | string) => {
    this.walletHistoryStoreId = storeId;
    this.walletTransactionsPage = 1;
    this.fetchWalletTransactions(1).catch(() => {});
  };

  closeWalletHistoryDrawer = () => {
    this.walletHistoryStoreId = null;
    this.walletTransactions = [];
    this.walletTransactionsLoading = false;
    this.walletTransactionsError = null;
    this.walletTransactionsPage = 1;
    this.walletTransactionsTotalCount = 0;
  };

  setWalletTransactionsPage = (page: number) => {
    this.walletTransactionsPage = page;
  };

  fetchWalletTransactions = async (page_no?: number) => {
    if (this.walletHistoryStoreId == null) {
      throw new Error("Store ID is required");
    }

    const page = page_no ?? this.walletTransactionsPage;

    runInAction(() => {
      this.walletTransactionsLoading = true;
      this.walletTransactionsError = null;
    });

    try {
      const response = await this.swagStoreAPI.getWalletTransactions({
        store_id: this.walletHistoryStoreId,
        page_no: page,
        limit: this.walletTransactionsLimit,
        type: "all",
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch transactions");
      }

      const result =
        response.result as PaginatedAPIResponse<SwagstoreWalletTransaction>;

      runInAction(() => {
        this.walletTransactions = result?.list || [];
        this.walletTransactionsPage = result?.page_no || page;
        this.walletTransactionsLimit =
          result?.limit || this.walletTransactionsLimit;
        this.walletTransactionsTotalCount = result?.total_count || 0;
      });
    } catch (error: any) {
      runInAction(() => {
        this.walletTransactions = [];
        this.walletTransactionsError =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load wallet transactions.";
      });
      throw error;
    } finally {
      runInAction(() => {
        this.walletTransactionsLoading = false;
      });
    }
  };

  refreshWalletTransactionsIfOpen = () => {
    if (this.walletHistoryStoreId == null) return;
    this.fetchWalletTransactions(1).catch(() => {});
  };

  fetchAddBalanceModalWallet = async (storeId: number | string) => {
    runInAction(() => {
      this.addBalanceWalletLoading = true;
      this.addBalanceWalletError = null;
      this.addBalanceCurrentBalance = null;
    });

    const store = this.stores.find(
      (s) => String(s.store_id ?? s.id) === String(storeId),
    );
    const customerId =
      store?.customer_id != null ? Number(store.customer_id) : null;

    if (
      store?.wallet_balance != null &&
      !Number.isNaN(Number(store.wallet_balance))
    ) {
      const balance = Number(store.wallet_balance);
      runInAction(() => {
        this.addBalanceCurrentBalance = balance;
        if (
          customerId != null &&
          Number.isFinite(customerId) &&
          customerId > 0
        ) {
          this.applyWalletBalanceToStores(customerId, balance);
        }
        this.addBalanceWalletLoading = false;
      });
      return;
    }

    if (customerId == null || !Number.isFinite(customerId) || customerId <= 0) {
      runInAction(() => {
        this.addBalanceWalletError =
          "Customer ID is required to load wallet balance.";
        this.addBalanceWalletLoading = false;
      });
      return;
    }

    try {
      const response = await this.swagStoreAPI.getWalletBalance(customerId);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch wallet balance");
      }

      const result = response.result;
      const balance = Number(result?.balance ?? 0);
      const resolvedCustomerId = Number(result?.customer_id ?? customerId);

      runInAction(() => {
        this.addBalanceCurrentBalance = Number.isFinite(balance) ? balance : 0;
        if (Number.isFinite(resolvedCustomerId) && resolvedCustomerId > 0) {
          this.applyWalletBalanceToStores(
            resolvedCustomerId,
            this.addBalanceCurrentBalance,
          );
        }
      });
    } catch (error: any) {
      runInAction(() => {
        this.addBalanceWalletError =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load wallet balance.";
      });
      throw error;
    } finally {
      runInAction(() => {
        this.addBalanceWalletLoading = false;
      });
    }
  };

  applyWalletBalanceToStores = (customerId: number, balance: number) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return;

    runInAction(() => {
      this.walletBalanceByCustomerId = {
        ...this.walletBalanceByCustomerId,
        [customerId]: balance,
      };
      this.stores = this.stores.map((store) => {
        const storeCustomerId =
          store.customer_id != null ? Number(store.customer_id) : null;
        if (storeCustomerId === customerId) {
          return {
            ...store,
            customer_id: customerId,
            wallet_balance: balance,
          };
        }
        return store;
      });
    });
  };

  getStoreWalletBalance = (store: {
    customer_id?: number;
    wallet_balance?: number;
  }): number | null => {
    if (
      store.wallet_balance != null &&
      !Number.isNaN(Number(store.wallet_balance))
    ) {
      return Number(store.wallet_balance);
    }
    const customerId =
      store.customer_id != null ? Number(store.customer_id) : null;
    if (
      customerId != null &&
      this.walletBalanceByCustomerId[customerId] != null
    ) {
      return this.walletBalanceByCustomerId[customerId];
    }
    return null;
  };

  addWalletBalance = async (payload: {
    amount: number;
    invoice_number: string;
    transaction_id: string;
  }): Promise<APIResponse> => {
    if (this.addBalanceStoreId == null) {
      throw new Error("Store ID is required");
    }

    try {
      this.addBalanceSubmitting = true;
      const response = await this.swagStoreAPI.addWalletBalance({
        store_id: this.addBalanceStoreId,
        amount: payload.amount,
        invoice_number: payload.invoice_number,
        transaction_id: payload.transaction_id,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to add balance");
      }

      const result = response.result;
      const customerId = Number(result?.customer_id);
      const balance = Number(result?.balance);

      if (Number.isFinite(customerId) && customerId > 0) {
        this.applyWalletBalanceToStores(
          customerId,
          Number.isFinite(balance) ? balance : 0,
        );
      }

      runInAction(() => {
        this.addBalanceCurrentBalance = Number.isFinite(balance) ? balance : 0;
      });

      if (
        this.walletHistoryStoreId != null &&
        String(this.walletHistoryStoreId) === String(this.addBalanceStoreId)
      ) {
        this.refreshWalletTransactionsIfOpen();
      }

      return response;
    } catch (error) {
      throw error;
    } finally {
      runInAction(() => {
        this.addBalanceSubmitting = false;
      });
    }
  };

  getAllStores = async (
    page_no?: number,
    limit?: number,
    search_text?: string,
  ): Promise<void> => {
    try {
      this.loadingStores = true;
      const searchTerm =
        search_text !== undefined ? search_text : this.storeListingSearchText;
      const response = await this.swagStoreAPI.getAllStores(
        page_no || this.storeListingPage,
        limit || this.storeListingLimit,
        searchTerm,
      );
      runInAction(() => {
        this.stores = response.list || [];
        this.storeListingPage = response.page_no || 1;
        this.storeListingLimit = response.limit || 30;
        this.storeListingTotalPages = response.total_pages || 0;
        this.storeListingTotalCount = response.total_count || 0;
        this.loadingStores = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.loadingStores = false;
      });
      throw error;
    }
  };

  updateStoreStatus = async (
    storeId: number | string,
    isActive: boolean,
  ): Promise<void> => {
    const index = this.stores.findIndex(
      (s) => String(s.store_id ?? s.id) === String(storeId),
    );
    if (index === -1) return;

    const previousState = this.stores[index].is_active;
    runInAction(() => {
      this.stores[index] = { ...this.stores[index], is_active: isActive };
    });

    try {
      await this.swagStoreAPI.updateStoreStatus(storeId, isActive);
    } catch (error) {
      runInAction(() => {
        this.stores[index] = {
          ...this.stores[index],
          is_active: previousState,
        };
      });
      throw error;
    }
  };

  uploadProductsPacksCSV = async (file: File): Promise<APIResponse> => {
    try {
      if (!this.storeId) {
        throw new Error("Store ID is required");
      }
      const response = await this.swagStoreAPI.uploadProductsPacksCSV(
        this.storeId,
        file,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  fetchAuthenticationMethods = async (): Promise<void> => {
    try {
      this.loadingAuthenticationMethods = true;
      const response = await this.swagStoreAPI.fetchAuthenticationMethods();
      runInAction(() => {
        this.authenticationMethods = response.result || [];
        this.loadingAuthenticationMethods = false;
      });
    } catch (error) {
      this.loadingAuthenticationMethods = false;
      throw error;
    }
  };

  setSelectedAuthenticationMethods = (methodIds: number[]) => {
    this.selectedAuthenticationMethods = methodIds;
  };

  toggleAuthenticationMethod = (methodId: number) => {
    if (this.selectedAuthenticationMethods?.includes(methodId)) {
      this.selectedAuthenticationMethods =
        this.selectedAuthenticationMethods?.filter((id) => id !== methodId);
    } else {
      this.selectedAuthenticationMethods = [
        ...this.selectedAuthenticationMethods,
        methodId,
      ];
    }
  };
}
